<script setup lang="ts">
import { ExternalLink as LucideExternalLink } from 'lucide-vue-next'
import AirunLogo from './AirunLogo.vue'

type AirunLinkLocation = 'tts_button' | 'news_disclaimer'

interface Props {
  // Where the link sits: picks the UTM campaign and is reported with the click.
  location: AirunLinkLocation
  // Matches the mark to the surrounding block: the external-link badge is
  // punched out in the block's own background colour.
  badgeColorClass?: string
  sizeClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  badgeColorClass: 'bg-[#0a0e27]',
  sizeClass: 'h-10 w-10'
})

const { trackEvent } = useAnalytics()

// Each placement credits a different AIRUN feature: speech synthesis or translation.
const UTM_CAMPAIGNS: Record<AirunLinkLocation, string> = {
  tts_button: 'tts',
  news_disclaimer: 'translation'
}

const href = computed(() => {
  const url = new URL('https://airun.kg/')
  url.searchParams.set('utm_source', 'banks.kg')
  url.searchParams.set('utm_medium', 'referral')
  url.searchParams.set('utm_campaign', UTM_CAMPAIGNS[props.location])
  return url.toString()
})

const trackClick = () => {
  trackEvent('partner_link_click', { partner: 'airun', link_location: props.location })
}
</script>

<template>
  <!-- Links out to the service behind the feature. -->
  <a
    :href="href"
    target="_blank"
    rel="noopener noreferrer"
    class="relative shrink-0 rounded-lg transition-opacity duration-200 hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
    :title="$t('ui.tts_controls.airun_link_title')"
    @click="trackClick"
  >
    <AirunLogo :class="sizeClass" />

    <!-- Sits on the mark's corner rather than after it: the row is width capped,
         and an inline icon would compete with the label. -->
    <LucideExternalLink
      class="absolute -right-1 -top-1 h-4 w-4 rounded-full p-px text-white/70"
      :class="badgeColorClass"
    />
  </a>
</template>
